import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROUTES } from '../utils/constants';
import apiService from '../services/api';

const BundlesPage = () => {
  const { user } = useAuth();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [stats, setStats] = useState({
    totalContent: 0,
    averageProgress: 0,
    completedContent: 0,
  });

  useEffect(() => {
    loadBundles();
    loadUnreadCount();
  }, []);

  const loadUnreadCount = async () => {
    try {
      const readNotificationIds = JSON.parse(localStorage.getItem('readNotifications') || '[]');
      const generatedNotifications = [];
      
      const purchases = await apiService.getUserPurchases();
      purchases.forEach((purchase) => {
        const notifId = `purchase-${purchase.id}`;
        generatedNotifications.push({
          id: notifId,
          read: readNotificationIds.includes(notifId)
        });
      });

      const manualPayments = await apiService.getUserManualPayments();
      manualPayments.forEach((payment) => {
        const notifId = `payment-${payment.id}`;
        generatedNotifications.push({
          id: notifId,
          read: readNotificationIds.includes(notifId)
        });
      });

      const unread = generatedNotifications.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error loading notification count:', err);
    }
  };

  const loadBundles = async () => {
    try {
      setLoading(true);
      const userBundles = await apiService.getUserBundles();
      setBundles(userBundles);

      // Calculate stats from all bundle items
      let totalItems = 0;
      userBundles.forEach(bundle => {
        totalItems += bundle.item_count;
      });

      // For now, progress is from the main purchases page
      // We'll leave it simple here
      setStats({
        totalContent: totalItems,
        averageProgress: 0, // Can be calculated from progress API later
        completedContent: 0,
      });
    } catch (err) {
      console.error('Error loading bundles:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-white to-secondary/10 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text3 font-bold">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-white to-secondary/10 py-8 pb-24 md:pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <Link
            to={ROUTES.DASHBOARD}
            className="inline-flex items-center gap-2 text-primary hover:underline font-bold mb-3 md:mb-4 text-sm md:text-base"
          >
            <i className="fas fa-arrow-right"></i>
            العودة للوحة التحكم
          </Link>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-black text-text mb-3 md:mb-4 flex items-center justify-center gap-2 md:gap-3">
            <i className="fas fa-boxes text-primary text-xl md:text-3xl"></i>
            حزمي المشتراة
          </h1>
          <p className="text-text3 text-sm md:text-lg">جميع الحزم التعليمية التي اشتريتها بأسعار مخفضة</p>
        </div>

        {/* Stats Cards - All in one row on mobile */}
        <div className="grid grid-cols-3 gap-2 md:gap-6 mb-6 md:mb-8">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl md:rounded-2xl lg:rounded-3xl p-3 md:p-6 shadow-xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-1 md:mb-2">
              <div className="text-2xl md:text-5xl font-black order-2 md:order-1">{stats.totalContent}</div>
              <i className="fas fa-book-open text-xl md:text-4xl opacity-50 order-1 md:order-2 mb-1 md:mb-0"></i>
            </div>
            <div className="text-[10px] md:text-sm font-semibold opacity-90 leading-tight">محتوى مشترى</div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl md:rounded-2xl lg:rounded-3xl p-3 md:p-6 shadow-xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-1 md:mb-2">
              <div className="text-2xl md:text-5xl font-black order-2 md:order-1">{stats.averageProgress}%</div>
              <i className="fas fa-chart-line text-xl md:text-4xl opacity-50 order-1 md:order-2 mb-1 md:mb-0"></i>
            </div>
            <div className="text-[10px] md:text-sm font-semibold opacity-90 leading-tight">التقدم الكلي</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl md:rounded-2xl lg:rounded-3xl p-3 md:p-6 shadow-xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-1 md:mb-2">
              <div className="text-2xl md:text-5xl font-black order-2 md:order-1">{stats.completedContent}</div>
              <i className="fas fa-trophy text-xl md:text-4xl opacity-50 order-1 md:order-2 mb-1 md:mb-0"></i>
            </div>
            <div className="text-[10px] md:text-sm font-semibold opacity-90 leading-tight">محتوى مكتمل</div>
          </div>
        </div>

        {/* Bundles List */}
        {bundles.length === 0 ? (
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-lg p-8 md:p-12 text-center">
            <i className="fas fa-boxes text-4xl md:text-6xl text-text3 mb-3 md:mb-4"></i>
            <h3 className="text-xl md:text-2xl font-black text-text mb-2">لا توجد حزم مشتراة</h3>
            <p className="text-sm md:text-base text-text3 mb-4 md:mb-6">اشترِ حزمة من الدورات أو القصص واحصل على خصم يصل إلى 25%!</p>
            <Link
              to={ROUTES.COURSES}
              className="inline-block px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-primary to-secondary text-white font-black rounded-xl md:rounded-2xl shadow-lg hover:shadow-xl transition-all text-sm md:text-base"
            >
              تصفح الدورات
            </Link>
          </div>
        ) : (
          <div className="space-y-4 md:space-y-6">
            {bundles.map((bundle, index) => (
              <div
                key={bundle.id}
                className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-6 border-2 border-purple-200 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-2xl font-black text-text">
                        حزمة #{index + 1} - {bundle.item_count} محتويات
                      </h3>
                      {/* Payment Method Badge */}
                      {bundle.payment_method === 'ccp_manual' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-bold rounded-full shadow-md">
                          <i className="fas fa-money-bill-wave"></i>
                          دفع يدوي CCP
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-xs font-bold rounded-full shadow-md">
                          <i className="fas fa-credit-card"></i>
                          دفع أونلاين
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-text3">
                      تاريخ الشراء: {new Date(bundle.created_at).toLocaleDateString('ar-DZ')}
                    </p>
                  </div>
                  <div className="text-left">
                    <div className="text-3xl font-black text-purple-600">{bundle.total_price} دج</div>
                    <div className="text-sm font-bold text-green-600">
                      وفرت {bundle.discount_amount} دج ({bundle.discount_percent}%)
                    </div>
                  </div>
                </div>

                {/* Bundle Items */}
                {bundle.items && bundle.items.length > 0 && (
                  <div className="bg-white/60 rounded-2xl p-4 mt-4">
                    <div className="text-sm font-bold text-text mb-2">المحتويات:</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {bundle.items.map((item) => (
                        <div key={item.course_id} className="text-sm text-text2 flex items-center gap-2">
                          <i className="fas fa-check text-purple-600"></i>
                          <span>{item.course_title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Total Savings Card */}
            {bundles.length > 0 && (
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl p-6 text-center">
                <div className="text-sm font-semibold mb-1 flex items-center justify-center gap-2">
                  <i className="fas fa-piggy-bank"></i>
                  التوفير الإجمالي من الحزم
                </div>
                <div className="text-4xl font-black">
                  {bundles.reduce((sum, b) => sum + (parseFloat(b.discount_amount) || 0), 0).toFixed(0)} دج
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fixed Bottom Navigation - Mobile Only */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50 safe-area-bottom">
        <div className="grid grid-cols-3 gap-1 px-2 py-1">
          <Link
            to="/notifications"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-bell text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الإشعارات</span>
            {unreadCount > 0 && (
              <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Link>
          
          <Link
            to={ROUTES.DASHBOARD}
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors relative"
          >
            <i className="fas fa-home text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">لوحة التحكم</span>
          </Link>
          
          <Link
            to="/profile"
            className="flex flex-col items-center justify-center py-1.5 px-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="fas fa-user text-lg text-text2 mb-0.5"></i>
            <span className="text-[9px] font-bold text-text2">الملف الشخصي</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BundlesPage;
